/**
 * PO Line Detail Footer - fixed action bar for the PO line modal (CR-4703).
 *
 * Renders all contextual actions (Review Changes, Propose Change, Critical toggle,
 * Hold toggle, Cancel Change Request) on the left and the Close button on the right.
 *
 * CR-4705 round 2 rework: Accept/Decline are no longer the footer's primary
 * actions for change-request states. Instead a single "Review Changes"
 * button surfaces here whenever the line is awaiting the current viewer's
 * decision, and routes them into the Change Requests tab where the per-
 * field Accept/Reject controls live (added in CR-4688). The button hides
 * once the user is already on that tab to avoid the "click here to go
 * where you already are" anti-pattern.
 *
 * CR-8718: the same button now serves the side whose request was DECLINED,
 * replacing the Propose Change + Close Request pair it was offered here. Its
 * two answers (Acknowledge Decline, Amend Request) live on the Change Requests
 * tab beside the request they answer, as Accept / Reject do.
 */
import { AlertCircle, PauseCircle, XCircle, Calendar, CalendarCheck, CheckCircle, ArrowRight, FileWarning, ShieldAlert } from 'lucide-react';
import type { PurchaseOrderLine } from '../../lib/api';

interface POLineDetailFooterProps {
  line: PurchaseOrderLine;
  // CR-4705: hide the Review Changes button once the user is on the
  // Change Requests tab.
  activeTab: string;
  canToggleCritical: boolean;
  canToggleHold: boolean;
  canShowProposeChange: boolean;
  /** Walkthrough batch 2 (Aaron, 17 Sep 2026): the supplier's answer to a
   *  `new` order. The grid has had it all along as the tick that reads
   *  "Acknowledge"; this modal had no way to answer an order at all. */
  canAcknowledge: boolean;
  /** P10: the customer writing the three dates outright on a `new` line. */
  canSetDates: boolean;
  canShowWorkflowActions: boolean;
  isProposer: boolean;
  // CR-5551: supplier-only entry points for raising a Concession (CE) or
  // requesting a Production Permit (PE) directly against this line.
  canRaiseDeviation?: boolean;
  onRaiseConcession?: () => void;
  onRequestProductionPermit?: () => void;
  onToggleCritical: () => void;
  onToggleHold: () => void;
  onAcknowledge: () => void;
  onSetDates: () => void;
  onPropose: () => void;
  onSwitchToChangeRequests: () => void;
  onClose: () => void;
}

export function POLineDetailFooter({
  line,
  activeTab,
  canToggleCritical,
  canToggleHold,
  canShowProposeChange,
  canAcknowledge,
  canSetDates,
  canShowWorkflowActions,
  isProposer,
  canRaiseDeviation,
  onRaiseConcession,
  onRequestProductionPermit,
  onToggleCritical,
  onToggleHold,
  onAcknowledge,
  onSetDates,
  onPropose,
  onSwitchToChangeRequests,
  onClose,
}: POLineDetailFooterProps) {
  return (
    <div className="border-t p-4 flex items-center justify-between gap-3 bg-muted/10 flex-wrap">
      <div className="flex flex-wrap gap-2">
        {canShowWorkflowActions && activeTab !== 'change-requests' && (
          <button
            onClick={onSwitchToChangeRequests}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <ArrowRight className="h-4 w-4" />
            Review Changes
          </button>
        )}
        {canAcknowledge && (
          <button
            onClick={onAcknowledge}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm font-medium transition-colors"
            title="Acknowledge this order line"
          >
            <CheckCircle className="h-4 w-4" />
            Acknowledge
          </button>
        )}
        {canSetDates && (
          <button
            onClick={onSetDates}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center gap-2 text-sm font-medium transition-colors"
            title="Set the dates directly — only while the line is new"
          >
            <CalendarCheck className="h-4 w-4" />
            Update Dates
          </button>
        )}
        {canShowProposeChange && (
          <button
            onClick={onPropose}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <Calendar className="h-4 w-4" />
            Propose Change
          </button>
        )}
        {isProposer && (
          <button
            onClick={onSwitchToChangeRequests}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <XCircle className="h-4 w-4" />
            Cancel Change Request
          </button>
        )}
        {canToggleCritical && (
          <button
            onClick={onToggleCritical}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
              line.is_critical
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
            title={line.is_critical ? 'Click to remove critical flag' : 'Click to mark as critical'}
          >
            <AlertCircle className="h-4 w-4" />
            {line.is_critical ? 'Unmark as Critical' : 'Mark as Critical'}
          </button>
        )}
        {canToggleHold && (
          <button
            onClick={onToggleHold}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
              line.on_hold
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
            title={line.on_hold ? 'Click to release from hold' : 'Click to put on hold'}
          >
            <PauseCircle className="h-4 w-4" />
            {line.on_hold ? 'Take Off Hold' : 'Put On Hold'}
          </button>
        )}
        {/* CR-5551: PO-line-driven Concession + Production Permit entry points.
            Spec: "concessions are initiated FROM the PO line, not independently
            from the Quality menu." Supplier-only — customers approve, they
            don't raise. */}
        {canRaiseDeviation && onRequestProductionPermit && (
          <button
            onClick={onRequestProductionPermit}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium transition-colors"
            title="Request a Production Permit (PE) for this line"
          >
            <ShieldAlert className="h-4 w-4" />
            Request Production Permit
          </button>
        )}
        {canRaiseDeviation && onRaiseConcession && (
          <button
            onClick={onRaiseConcession}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center gap-2 text-sm font-medium transition-colors"
            title="Raise a Concession (CE) for this line"
          >
            <FileWarning className="h-4 w-4" />
            Raise Concession
          </button>
        )}
      </div>
      <button
        onClick={onClose}
        className="px-4 py-2 border border-border rounded-lg hover:bg-muted font-medium"
      >
        Close
      </button>
    </div>
  );
}
